import React, { useState, useEffect } from 'react';
import { Device } from '../../types';
import ZigbeeCapabilitiesPanel from '../device-controls/ZigbeeCapabilitiesPanel';

interface Props {
    device: Device;
    onChange: (d: Device) => void;
    onSubmit: (e: React.FormEvent) => void;
    onCancel: () => void;
}

const EditDeviceForm: React.FC<Props> = ({ device, onChange, onSubmit, onCancel }) => {
    const [showZigbeeConfig, setShowZigbeeConfig] = useState(false);
    const [showDeviceSettings, setShowDeviceSettings] = useState(false);
    const isBattery = device.type === 'temperature' || device.type === 'humidity' || 
                      device.type === 'motion' || device.type === 'contact' ||
                      (device.battery_type !== null && device.battery_type !== '');
    
    // Local settings state
    const [settings, setSettings] = useState<Record<string, any>>(device.settings || {});
    
    // Sync settings back to device object whenever they change
    useEffect(() => {
        onChange({ ...device, settings });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    return (
        <div style={{
            backgroundColor: '#fff3cd',
            padding: '20px',
            borderRadius: '4px',
            marginBottom: '20px',
            border: '1px solid #ffc107'
        }}>
            <h3>Ändra enhet</h3>
            <form onSubmit={onSubmit}>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Enhetens namn:
                </label>
                <input
                    type="text"
                    value={device.name}
                    onChange={(e) => onChange({ ...device, name: e.target.value })}
                    required
                    style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                    }}
                />
            </div>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Typ av enhet:
                </label>
                <select
                    value={device.type}
                    onChange={(e) => onChange({ ...device, type: e.target.value })}
                    style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                    }}
                >
                    <option value="light">Lampa</option>
                    <option value="switch">Strömbrytare/uttag</option>
                    <option value="temperature">Temperaturgivare</option>
                    <option value="humidity">Fuktgivare</option>
                    <option value="motion">Rörelsevakt</option>
                    <option value="contact">Dörr-/fönstergivare</option>
                    <option value="dimmer">Dimmer</option>
                    <option value="thermostat">Termostat</option>
                    <option value="power_meter">Elmätare</option>
                    <option value="io_module">IO-modul</option>
                </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#666' }}>
                    Enhets-ID (går inte att ändra):
                </label>
                <code style={{
                    backgroundColor: 'white',
                    padding: '8px',
                    borderRadius: '4px',
                    display: 'block',
                    fontSize: '14px',
                    border: '1px solid #ddd'
                }}>
                    {device.id}
                </code>
            </div>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Batterityp:
                </label>
                <select
                    value={device.battery_type ?? ''}
                    onChange={(e) => onChange({ ...device, battery_type: e.target.value || null })}
                    style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                    }}
                >
                    <option value="">— okänt / gäller inte —</option>
                    <option value="CR2032">CR2032</option>
                    <option value="CR2450">CR2450</option>
                    <option value="CR2477">CR2477</option>
                    <option value="CR123A">CR123A</option>
                    <option value="CR1632">CR1632</option>
                    <option value="AAA">AAA</option>
                    <option value="AA">AA</option>
                </select>
            </div>
            <button
                type="submit"
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px',
                    fontWeight: 'bold'
                }}
            >
                Spara
            </button>
            <button
                type="button"
                onClick={onCancel}
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px'
                }}
            >
                Avbryt
            </button>
            <button
                type="button"
                onClick={() => setShowZigbeeConfig(!showZigbeeConfig)}
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px'
                }}
            >
                {showZigbeeConfig ? '▼ Dölj' : '▶'} Zigbee-inställningar
            </button>
            {(device.type === 'temperature' || device.type === 'humidity' || device.type === 'motion' || device.type === 'io_module' || device.type === 'power_meter') && (
                <button
                    type="button"
                    onClick={() => setShowDeviceSettings(!showDeviceSettings)}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    {showDeviceSettings ? '▼ Dölj' : '▶'} Enhetsinställningar
                </button>
            )}
        </form>

        {showZigbeeConfig && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #ffc107' }}>
                <ZigbeeCapabilitiesPanel deviceId={device.ieee_address ?? device.id} isBattery={isBattery} />
            </div>
        )}
        
        {showDeviceSettings && (
            <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #17a2b8' }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Inställningar för den här enheten</h4>
                
                {/* Temperature/Humidity Sensor Settings */}
                {(device.type === 'temperature' || device.type === 'humidity') && (
                    <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ marginBottom: '10px', color: '#666' }}>Så ofta enheten rapporterar</h5>
                        <p style={{ fontSize: '13px', color: '#888', marginBottom: '15px' }}>
                            Ställ in hur ofta givaren skickar värden. Lämna tomt för att använda standardinställningen.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Temperatur, minst (s):</label>
                                <input
                                    type="number"
                                    placeholder="t.ex. 300"
                                    value={settings.tempMin ?? ''}
                                    onChange={(e) => setSettings({ ...settings, tempMin: e.target.value ? Number(e.target.value) : null })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Temperatur, högst (s):</label>
                                <input
                                    type="number"
                                    placeholder="t.ex. 3600"
                                    value={settings.tempMax ?? ''}
                                    onChange={(e) => setSettings({ ...settings, tempMax: e.target.value ? Number(e.target.value) : null })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Temperaturändring (1/100 °C):</label>
                                <input
                                    type="number"
                                    placeholder="t.ex. 50 (0,5 °C)"
                                    value={settings.tempChange ?? ''}
                                    onChange={(e) => setSettings({ ...settings, tempChange: e.target.value ? Number(e.target.value) : null })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Fukt, minst (s):</label>
                                <input
                                    type="number"
                                    placeholder="t.ex. 300"
                                    value={settings.humMin ?? ''}
                                    onChange={(e) => setSettings({ ...settings, humMin: e.target.value ? Number(e.target.value) : null })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Fukt, högst (s):</label>
                                <input
                                    type="number"
                                    placeholder="t.ex. 3600"
                                    value={settings.humMax ?? ''}
                                    onChange={(e) => setSettings({ ...settings, humMax: e.target.value ? Number(e.target.value) : null })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>Fuktändring (1/100 %):</label>
                                <input
                                    type="number"
                                    placeholder="t.ex. 300 (3 %)"
                                    value={settings.humChange ?? ''}
                                    onChange={(e) => setSettings({ ...settings, humChange: e.target.value ? Number(e.target.value) : null })}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Motion Sensor Settings */}
                {device.type === 'motion' && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Tid innan rörelse nollställs (sekunder):</label>
                        <input
                            type="number"
                            placeholder="t.ex. 90"
                            value={settings.occupancy_timeout_seconds ?? ''}
                            onChange={(e) => setSettings({ ...settings, occupancy_timeout_seconds: e.target.value ? Number(e.target.value) : null })}
                            style={{ width: '200px', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>Så lång tid går innan rörelselarmet släcks av sig självt</p>
                    </div>
                )}
                
                {/* IO Module Settings */}
                {device.type === 'io_module' && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Hur ofta modulen läses av (sekunder):</label>
                        <input
                            type="number"
                            placeholder="t.ex. 60"
                            value={settings.poll_interval ?? ''}
                            onChange={(e) => setSettings({ ...settings, poll_interval: e.target.value ? Number(e.target.value) : null })}
                            style={{ width: '200px', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>Hur ofta kanalerna på IO-modulen läses av</p>
                    </div>
                )}
                
                {/* Power Meter Settings */}
                {device.type === 'power_meter' && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Pulser per kWh:</label>
                        <input
                            type="number"
                            placeholder="t.ex. 1000"
                            value={settings.pulses_per_kwh ?? ''}
                            onChange={(e) => setSettings({ ...settings, pulses_per_kwh: e.target.value ? Number(e.target.value) : null })}
                            style={{ width: '200px', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <p style={{ fontSize: '12px', color: '#888', marginTop: '5px' }}>Mätarens kalibreringsvärde</p>
                    </div>
                )}
            </div>
        )}
    </div>
    );
};

export default EditDeviceForm;
